import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { SessionContext } from "../../types/sessionContext";
import useVoiceConversation from "@/hooks/useVoiceConversation";
import CallButton from "../../components/callbutton";
import { useRealtimeContext } from "../../context/RealtimeContext";
import ReactMarkdown from "react-markdown";
import { throttledLog } from "../../utils/throttledLog";

interface ChatMessage {
    role: "assistant" | "user";
    content: string;
}

interface AITabsProps {
    currentStep: number;
    sessionContext: SessionContext;
    updateSessionContext: (newValue: SessionContext) => void;
}

const AITabs: React.FC<AITabsProps> = ({ currentStep }) => {
    const [activeTab, setActiveTab] = useState<"call" | "chat">("chat");
    const { t } = useTranslation();
    const { isRecording, onToggleRecording, assistantPartial, latestTranscript, translateText } = useVoiceConversation();
    const { updateSessionContext: updateRealtimeContext, sessionContext } = useRealtimeContext();
    const prevPromptRef = useRef(sessionContext.layout.assistant.lastPrompt);

    const [phoneNumber, setPhoneNumber] = useState("");
    // Change from static to dynamic call messages
    const [callMessages, setCallMessages] = useState<ChatMessage[]>([]);

    // Use a format similar to AssistantPanel
    const [messages, setMessages] = useState<{ sender: "user" | "assistant"; text: string }[]>([]);

    const [draftMessage, setDraftMessage] = useState("");
    const [translatedText, setTranslatedText] = useState("");
    const [showTranslation, setShowTranslation] = useState(false);
    const [hoveredMessageIndex, setHoveredMessageIndex] = useState<number | null>(null);

    const handleTabChange = (tab: "call" | "chat") => {
        setActiveTab(tab);
    };

    // Update the useEffect for step changes to also consider prompt changes
    useEffect(() => {
        // Log the step change with the current prompt
        throttledLog("AI_TABS", `Step changed to ${currentStep}, prompt: ${sessionContext.layout.assistant.lastPrompt}`);
    }, [currentStep, sessionContext.layout.assistant.lastPrompt]);

    // Update both message formats when transcript changes
    useEffect(() => {
        if (!latestTranscript) return;

        if (latestTranscript.startsWith("**User:**")) {
            const userText = latestTranscript.replace("**User:**", "").trim();
            if (userText) {
                // Update chat tab messages
                setMessages(prev => {
                    const lastMessage = prev[prev.length - 1];
                    if (lastMessage?.sender === "user") {
                        return [...prev.slice(0, -1), { sender: "user", text: userText }];
                    } else {
                        return [...prev, { sender: "user", text: userText }];
                    }
                });

                // Also update call tab messages
                setCallMessages(prev => {
                    const lastMessage = prev[prev.length - 1];
                    if (lastMessage?.role === "user") {
                        return [...prev.slice(0, -1), { role: "user", content: userText }];
                    } else {
                        return [...prev, { role: "user", content: userText }];
                    }
                });
            }
        } else if (latestTranscript.startsWith("**Assistant:**")) {
            const assistantText = latestTranscript.replace("**Assistant:**", "").trim();
            if (assistantText) {
                // Update chat tab messages
                setMessages(prev => {
                    const lastMessage = prev[prev.length - 1];
                    if (lastMessage?.sender === "assistant") {
                        return [...prev.slice(0, -1), { sender: "assistant", text: assistantText }];
                    } else {
                        return [...prev, { sender: "assistant", text: assistantText }];
                    }
                });

                // Also update call tab messages
                setCallMessages(prev => {
                    const lastMessage = prev[prev.length - 1];
                    if (lastMessage?.role === "assistant") {
                        return [...prev.slice(0, -1), { role: "assistant", content: assistantText }];
                    } else {
                        return [...prev, { role: "assistant", content: assistantText }];
                    }
                });
            }
        }
    }, [latestTranscript]);

    // Process assistant partial text for streaming in both formats
    useEffect(() => {
        if (!assistantPartial) return;

        // Update chat messages
        setMessages(prev => {
            const lastMessage = prev[prev.length - 1];

            if (lastMessage?.sender === "assistant") {
                return [...prev.slice(0, -1), { ...lastMessage, text: assistantPartial }];
            } else {
                return [...prev, { sender: "assistant", text: assistantPartial }];
            }
        });

        // Update call messages
        setCallMessages(prev => {
            const lastMessage = prev[prev.length - 1];

            if (lastMessage?.role === "assistant") {
                return [...prev.slice(0, -1), { ...lastMessage, content: assistantPartial }];
            } else {
                return [...prev, { role: "assistant", content: assistantPartial }];
            }
        });
    }, [assistantPartial]);

    // Synchronize latestTranscript with session context
    useEffect(() => {
        if (latestTranscript) {
            updateRealtimeContext(prev => {
                const updated = structuredClone(prev);
                if (updated.layout.workflow.steps[currentStep]) {
                    updated.layout.workflow.steps[currentStep].chat.latestTranscript = latestTranscript;
                }
                return updated;
            }, "transcript-update");
        }
    }, [latestTranscript, currentStep, updateRealtimeContext]);

    const handleSendMessage = () => {
        if (!draftMessage.trim()) return;

        // Add user message
        setMessages(prev => [...prev, { sender: "user", text: draftMessage.trim() }]);
        setDraftMessage("");

        // Simulate AI response
        setTimeout(() => {
            setMessages(prev => [...prev, { sender: "assistant", text: "Let me check that for you..." }]);
        }, 1000);
    };

    // Handle recording with interrupt tracking
    const handleToggleRecording = async () => {
        await onToggleRecording();
        // Increment interrupts in session context
        updateRealtimeContext(prev => {
            const updated = structuredClone(prev);
            if (updated.layout.workflow.steps[currentStep]) {
                updated.layout.workflow.steps[currentStep].chat.interrupts += 1;
            }
            return updated;
        }, "interrupt-counter");
    };

    // Translation handlers
    async function handleHover(text: string, index: number) {
        try {
            setHoveredMessageIndex(index);
            const translated = await translateText(text, t("common.locale", "en"));
            setTranslatedText(translated);
            setShowTranslation(true);
        } catch (error) {
            console.error("[AITabs] Translation error:", error);
        }
    }

    function handleLeave() {
        setHoveredMessageIndex(null);
        setTranslatedText("");
        setShowTranslation(false);
    }

    // Save messages to context
    useEffect(() => {
        const contextMessages = messages.map(msg => ({
            sender: msg.sender,
            text: msg.text
        }));

        updateRealtimeContext(prev => {
            const updated = structuredClone(prev);
            if (updated.layout.workflow.steps[currentStep]) {
                updated.layout.workflow.steps[currentStep].chat.history = contextMessages;
            }
            return updated;
        }, "messages-update");
    }, [messages, currentStep, updateRealtimeContext]);

    useEffect(() => {
        // Only log when the assistant's prompt changes
        const currentPrompt = sessionContext.layout.assistant.lastPrompt;
        throttledLog("AI_TABS", `Using prompt: ${currentPrompt} for step ${currentStep}`);

        // When the prompt changes, clear and re-initialize messages
        if (currentPrompt !== prevPromptRef.current) {
            setMessages([]);
            prevPromptRef.current = currentPrompt;
        }
    }, [sessionContext.layout.assistant.lastPrompt, currentStep]);

    return (
        <div className="relative flex h-full min-h-0 flex-col overflow-hidden pb-20">
            <div className="mb-2 flex">
                <button
                    onClick={() => handleTabChange("call")}
                    className={`border-b-2 px-4 py-2 ${
                        activeTab === "call" ? "border-primary font-semibold text-primary" : "border-transparent text-gray-500"
                    }`}
                >
                    {t("base_demo.aiTabs.callTab")}
                </button>
                <button
                    onClick={() => handleTabChange("chat")}
                    className={`ml-4 border-b-2 px-4 py-2 ${
                        activeTab === "chat" ? "border-primary font-semibold text-primary" : "border-transparent text-gray-500"
                    }`}
                >
                    {t("base_demo.aiTabs.chatTab")}
                </button>
            </div>

            {activeTab === "call" && (
                <div className="flex min-h-0 flex-grow flex-col">
                    {/* Controls (do not scroll) */}
                    <div className="flex flex-none flex-shrink-0 flex-col space-y-2 pb-2">
                        <button onClick={handleToggleRecording} className={`mb-2 rounded ${isRecording ? "bg-red-500" : "bg-primary"} px-4 py-2 text-white`}>
                            {isRecording ? t("base_demo.app.stopConversation") : t("base_demo.aiTabs.startConversation")}
                        </button>
                        <div className="font-medium">{t("base_demo.aiTabs.callbackText")}</div>
                        <input
                            type="tel"
                            placeholder={t("base_demo.aiTabs.phonePlaceholder")}
                            className="w-40 rounded border p-2"
                            value={phoneNumber}
                            onChange={e => setPhoneNumber(e.target.value)}
                        />
                        <CallButton phoneNumber={phoneNumber} />
                    </div>
                    {/* Scrollable messages area */}
                    <div className="flex min-h-0 flex-1 flex-col overflow-auto rounded border-2 border-gray-200 p-2">
                        {callMessages.map((msg, i) => (
                            <div
                                key={i}
                                className={`relative mb-2 max-w-[80%] rounded px-3 py-2 text-sm ${msg.role === "assistant" ? "self-start bg-gray-100 text-gray-800" : "self-end bg-blue-100 text-gray-800"}`}
                                onMouseEnter={() => handleHover(msg.content, i)}
                                onMouseLeave={handleLeave}
                            >
                                <strong>{msg.role === "assistant" ? "Assistant" : "You"}:</strong> {msg.content}
                                {showTranslation && hoveredMessageIndex === i && (
                                    <div className="absolute left-0 z-10 mt-1 max-w-[300px] rounded border border-gray-300 bg-white p-2 shadow-md">
                                        {translatedText}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {activeTab === "chat" && (
                <div className="flex min-h-0 flex-grow flex-col">
                    <div className="flex min-h-0 flex-grow flex-col overflow-auto rounded border-2 border-gray-200 p-2">
                        {messages.map((message, index) => (
                            <div
                                key={index}
                                className={`chat-message relative mb-2 max-w-[80%] rounded px-3 py-2 text-sm ${message.sender === "assistant" ? "self-start bg-gray-100 text-gray-800" : "self-end bg-blue-100 text-gray-800"}`}
                                onMouseEnter={() => handleHover(message.text, index)}
                                onMouseLeave={handleLeave}
                            >
                                <strong>{message.sender === "assistant" ? "Assistant" : "You"}:</strong>
                                <ReactMarkdown>{message.text}</ReactMarkdown>
                                {showTranslation && hoveredMessageIndex === index && (
                                    <div className="absolute left-0 z-10 mt-1 max-w-[300px] rounded border border-gray-300 bg-white p-2 shadow-md">
                                        <ReactMarkdown>{translatedText}</ReactMarkdown>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="flex flex-none items-center space-x-2 border-t pt-4">
                        <input
                            type="text"
                            placeholder={t("base_demo.aiTabs.chatPlaceholder")}
                            value={draftMessage}
                            onChange={e => setDraftMessage(e.target.value)}
                            className="flex-grow rounded border p-2"
                        />
                        <button onClick={handleSendMessage} className="rounded bg-primary px-4 py-2 text-white">
                            {t("base_demo.aiTabs.send")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AITabs;
